/*
  <adday-carousel>: a CSS scroll-snap carousel with optional arrows and dots.

  Markup (all inside the element):
  - [data-carousel-track]  the horizontally scrolling list
  - [data-carousel-slide]  each slide, a direct child of the track
  - [data-carousel-prev] / [data-carousel-next]  optional arrow buttons
  - [data-carousel-dot]  optional dot buttons, one per slide, in slide order

  Scrolling is native (swipe, trackpad, keyboard). Arrows move by one slide
  and are disabled at either end. Dots follow the scroll via an
  IntersectionObserver and scroll to their slide when clicked. Smooth
  scrolling is skipped when prefers-reduced-motion is set.
*/
(() => {
  if (customElements.get('adday-carousel')) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class AddayCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      if (!this.track) return;

      this.slides = Array.from(this.track.querySelectorAll(':scope > [data-carousel-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));
      this.prev = this.querySelector('[data-carousel-prev]');
      this.next = this.querySelector('[data-carousel-next]');
      this.visible = new Set();

      this.onPrev = () => this.step(-1);
      this.onNext = () => this.step(1);
      this.onScroll = () => this.updateState();
      this.onBlockSelect = (event) => {
        const index = this.slides.findIndex((slide) => slide.contains(event.target));
        if (index > -1) this.goTo(index, false);
      };

      this.prev?.addEventListener('click', this.onPrev);
      this.next?.addEventListener('click', this.onNext);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goTo(index));
      });

      if (this.dots.length) {
        this.observer = new IntersectionObserver((entries) => this.onIntersect(entries), {
          root: this.track,
          threshold: [0.6, 0.99],
        });
        this.slides.forEach((slide) => this.observer.observe(slide));
      }

      this.resizeObserver = new ResizeObserver(() => this.updateState());
      this.resizeObserver.observe(this.track);
      this.updateState();
    }

    disconnectedCallback() {
      this.prev?.removeEventListener('click', this.onPrev);
      this.next?.removeEventListener('click', this.onNext);
      this.track?.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
    }

    get behavior() {
      return reducedMotion.matches ? 'auto' : 'smooth';
    }

    slideOffset(index) {
      const slide = this.slides[index];
      return slide.offsetLeft - this.slides[0].offsetLeft;
    }

    goTo(index, smooth = true) {
      const slide = this.slides[index];
      if (!slide) return;
      this.track.scrollTo({
        left: this.slideOffset(index),
        behavior: smooth ? this.behavior : 'auto',
      });
    }

    step(direction) {
      const first = this.slides[0];
      const second = this.slides[1];
      const distance = second ? second.offsetLeft - first.offsetLeft : this.track.clientWidth;
      this.track.scrollBy({ left: distance * direction, behavior: this.behavior });
    }

    onIntersect(entries) {
      entries.forEach((entry) => {
        const index = this.slides.indexOf(entry.target);
        if (entry.intersectionRatio >= 0.6) {
          this.visible.add(index);
        } else {
          this.visible.delete(index);
        }
        if (index === this.slides.length - 1) {
          this.lastFullyVisible = entry.intersectionRatio >= 0.99;
        }
      });

      if (!this.visible.size) return;

      // The leftmost visible slide is active. Once the track reaches the end,
      // the last slide can never be leftmost, so it takes over when fully shown.
      let active = Math.min(...this.visible);
      if (this.lastFullyVisible && this.visible.size > 1) active = this.slides.length - 1;
      this.setActiveDot(active);
    }

    setActiveDot(active) {
      this.dots.forEach((dot, index) => {
        const isActive = index === active;
        dot.classList.toggle('is-active', isActive);
        if (isActive) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }

    // Arrows disable at either end. "is-static" marks a row that fits without
    // scrolling, so sections can hide their arrows or dots.
    updateState() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const left = Math.abs(this.track.scrollLeft);
      if (this.prev) this.prev.disabled = left <= 2;
      if (this.next) this.next.disabled = left >= max - 2;
      this.classList.toggle('is-static', max <= 2);
    }
  }

  customElements.define('adday-carousel', AddayCarousel);
})();
